import type { Nullable, PatchPayload, Satisfy, NonNullableBy } from '@/types'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import type { PriorityEnum } from '@/data/models/domain/types'
import type UserPersonalInfo from '@/data/models/domain/UserPersonalInfo'
import type { PaginationUrlFilterScalarValueType, PaginationUrlFilterSingleType } from '@/services/tables/types'
import type { TmCountryCode, WeekDaysNumber } from '@/services/types'
import type { SenderSourceType } from '@/services/domain/senderSource/types'
import type { CustomFieldUpdateFormBody } from '@/services/domain/cusomFields/types'
import { isRecordUnknown } from '@/utils/typeGuards'
import type { customFieldFilterNamePrefix } from '@/utils/custom-fields/customFieldsFilter'
import type { CustomFieldPaginationUrlFilterScalarValueType } from '@/utils/custom-fields/types'
import type { ChatStatus } from '@/data/models/domain/chats/types'
import { isNil } from 'lodash-es'
import type { JSONContent } from '@tiptap/core'
import type AutomationLogPreview from '@/data/models/domain/automations/automationLogPreview'

export enum AutomationDelayUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
}

export enum AutomationFlowStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  DRAFT = 'draft',
  WAITING_FOR_STOP = 'waiting_for_stop',
  STOPPED = 'stopped',
  COMPLETED = 'completed',
  EXPIRED = 'expired',
}

export enum AutomationTimeType {
  ALWAYS = 'always',
  SCHEDULED = 'scheduled',
  DATE_RANGE = 'dateRange',
}

export type AutomationDelay = string // ISO 8601 duration, example: "PT1H" - 1 hour, "PT10M" - 10 minutes, "P10D" - 10 days
export const isAutomationDelay = (delay: unknown): delay is AutomationDelay => {
  return typeof delay === 'string'
}

export enum AdvancedTimeDelayMode {
  FOR_DURATION = 'forDuration',
  UNTIL_DATE = 'untilDate',
  CUSTOM_FIELD = 'customField',
}

export enum AdvancedTimeDelayBehavior {
  EXIT_FLOW = 'exitFlow',
  MARK_AS_FAILED = 'markAsFailed',
  CONTINUE = 'continue',
}

export enum AdvancedTimeDelayOffsetDirection {
  BEFORE = 'before',
  AFTER = 'after',
}

export interface AdvancedTimeDelayForDurationParams {
  type: AdvancedTimeDelayMode.FOR_DURATION
  delay: AutomationDelay
}

export interface AdvancedTimeDelayUntilDateParams {
  type: AdvancedTimeDelayMode.UNTIL_DATE
  date: string
  timezoneId: number
}

export interface AdvancedTimeDelayOffset {
  value: AutomationDelay
  direction: AdvancedTimeDelayOffsetDirection
}

export interface AdvancedTimeDelayCustomFieldParams {
  type: AdvancedTimeDelayMode.CUSTOM_FIELD
  customFieldId: number
  timezoneId: number
  time?: Nullable<string>
  behaviorWhenFieldIsEmpty: AdvancedTimeDelayBehavior
  behaviorWhenDateAlreadyPassed: AdvancedTimeDelayBehavior
  offset?: Nullable<AdvancedTimeDelayOffset>
}

export type AdvancedTimeDelayParams =
  | AdvancedTimeDelayForDurationParams
  | AdvancedTimeDelayUntilDateParams
  | AdvancedTimeDelayCustomFieldParams

export interface AutomationFlowScheduleTimeRange {
  from: string
  to: string
}

export interface AutomationFlowScheduleDaySchedule {
  day: number
  schedule: AutomationFlowScheduleTimeRange[]
}

export interface AutomationTemplateStep<T extends AutomationStepType = AutomationStepType> {
  localId: number
  parents: number[]
  title: string
  payload: AutomationFlowStepPayload<T>
}

export interface AutomationFlowScheduleSettings {
  timezoneId: string
  timeParams: AutomationFlowScheduleDaySchedule[]
}

export interface AutomationScheduleDateRange {
  from: string
  to: string
}

// oneOf discriminator (backend enum values)
export enum AutomationScheduleType {
  SELECTED_HOURS = 'selected_hours',
  DATE_RANGE = 'date_range',
}

// oneOf member: "Run on selected hours"
export interface AutomationScheduleSelectedHours {
  type: AutomationScheduleType.SELECTED_HOURS
  timezoneId: string
  timeParams: AutomationFlowScheduleDaySchedule[]
}

// oneOf member: "Run on specific date period"
export interface AutomationScheduleDateRangeSchedule {
  type: AutomationScheduleType.DATE_RANGE
  timezoneId: string
  dateRange: AutomationScheduleDateRange
  dailyHours: AutomationFlowScheduleTimeRange | null // null means "any time of the day"
}

export type AutomationSchedule = AutomationScheduleSelectedHours | AutomationScheduleDateRangeSchedule

// Discriminate structurally so it also works for legacy/template payloads without a `type` field.
export const isDateRangeSchedule = (schedule: AutomationSchedule): schedule is AutomationScheduleDateRangeSchedule => {
  return 'dateRange' in schedule
}

export enum AutomationFlowReenterSettingsType {
  NEVER = 'never',
  UNLIMITED = 'unlimited',
  ONCE_PER_PERIOD = 'oncePerPeriod',
  BLOCKED = 'blocked',
}

export interface AutomationFlowReenterSettingsBase<T extends AutomationFlowReenterSettingsType> {
  type: T
}

export interface AutomationFlowReenterSettingsOncePerPeriod extends AutomationFlowReenterSettingsBase<AutomationFlowReenterSettingsType.ONCE_PER_PERIOD> {
  period: {
    value: number
    unit: AutomationDelayUnit
  }
}

export type AutomationFlowReenterSettings =
  | AutomationFlowReenterSettingsOncePerPeriod
  | AutomationFlowReenterSettingsBase<AutomationFlowReenterSettingsType.NEVER>
  | AutomationFlowReenterSettingsBase<AutomationFlowReenterSettingsType.UNLIMITED>
  | AutomationFlowReenterSettingsBase<AutomationFlowReenterSettingsType.BLOCKED>

export enum AutomationFlowReenterPolicy {
  ALLOW = 'allow',
  DENY = 'deny',
  BLOCKED = 'blocked',
}

export type AutomationFlowSettings = {
  schedule: AutomationSchedule | null // null means always run
  reenterPolicy: AutomationFlowReenterPolicy // this field will be deleted in the future
  // Old flows may not have reenter settings
  reenterSettings: AutomationFlowReenterSettings | null
}

export type AutomationSplitBranchBasePayload = {
  sorting: number
}

export const AUTOMATION_STEP_ADDITIONAL_FILTERS_FIELD_NAME = 'contactFilters'
export type AutomationStepAdditionalFilters = PaginationUrlFilterSingleType & {
  [customFieldFilterNamePrefix]?: CustomFieldPaginationUrlFilterScalarValueType[]
}
export type AutomationStepPayloadWithAdditionalFilters = {
  [AUTOMATION_STEP_ADDITIONAL_FILTERS_FIELD_NAME]: AutomationStepAdditionalFilters[]
}

/**
 * Contact fields the "Contact updated" trigger can watch. Limited by the
 * backend AutomationContactWatchedFieldsDto
 */
export const automationContactWatchedFieldNames = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'whatsappPhone',
  'companyName',
] as const

export type AutomationContactWatchedFieldName = (typeof automationContactWatchedFieldNames)[number]

export type AutomationContactWatchedFields = Partial<
  Record<AutomationContactWatchedFieldName, PaginationUrlFilterScalarValueType | null>
>

export const isAutomationContactWatchedFieldName = (name: string): name is AutomationContactWatchedFieldName =>
  automationContactWatchedFieldNames.includes(name as AutomationContactWatchedFieldName)

export const isAutomationStepPayloadWithAdditionalFilters = <T extends AutomationFlowStepPayload<AutomationStepType>>(
  payload: T,
): payload is T & AutomationStepPayloadWithAdditionalFilters => {
  return AUTOMATION_STEP_ADDITIONAL_FILTERS_FIELD_NAME in payload
}

interface IAutomationFlowSendSmsSender {
  sourceType: SenderSourceType
  from: string
}
/**
 * null - default sender
 */
export type AutomationFlowSendSmsSender = IAutomationFlowSendSmsSender | null

export interface INewInboundMessageReceivedContentFilter {
  anyOf?: string[]
  allOf?: string[]
  noneOf?: string[]
  startsWith?: string
  startsWithAnyOf?: string[]
}

type AutomationChannelReplyActionPayload = {
  submitStatus: Nullable<ChatStatus>
  message: string
  fileIds: string[]
}

export type AutomationNewInboundLiveChatMessagePayload = {
  widgetFilter?: {
    in: string[]
  }
  contentFilter?: INewInboundMessageReceivedContentFilter
}

export type AutomationNewInboundWhatsappMessagePayload = {
  phones?: {
    phones: string[]
  }
  contentFilter?: INewInboundMessageReceivedContentFilter
  country?: {
    in: TmCountryCode[]
  }
}

export type AutomationNewInboundFacebookMessagePayload = {
  pageFilter?: {
    in: string[]
  }
  contentFilter?: INewInboundMessageReceivedContentFilter
}

export type AutomationNewInboundInstagramMessagePayload = {
  pageFilter?: {
    in: string[]
  }
  contentFilter?: INewInboundMessageReceivedContentFilter
}

export type AutomationWuehNewInboundSmsPayload = {
  phones?: {
    phones: string[]
  }
  content?: INewInboundMessageReceivedContentFilter
  country?: {
    in: string[]
  }
}

export enum AutomationStepType {
  // Triggers
  TRIGGER_TAG_ADDED_TO_CONTACT = 'tagIsAddedToContact',
  TRIGGER_CONTACT_ADDED_TO_LIST = 'contactIsAddedToGroup',
  TRIGGER_CONTACT_CREATED = 'contactCreated',
  TRIGGER_CONTACT_UPDATED = 'contactUpdated',
  TRIGGER_SUBSCRIBE_FORM_FILLED = 'subscribeFormSubmitted',
  TRIGGER_NEW_INBOUND_SMS_RECEIVED = 'newInboundMessageReceived',
  TRIGGER_NEW_INBOUND_EMAIL_RECEIVED = 'newInboundEmailReceived',
  TRIGGER_MANUALLY_SELECT_CONTACTS = 'manuallySelectedContacts',
  TRIGGER_NEW_INBOUND_LIVE_CHAT_MESSAGE_RECEIVED = 'newInboundLiveChatMessageReceived',
  TRIGGER_NEW_INBOUND_WHATSAPP_MESSAGE_RECEIVED = 'newInboundWhatsappMessageReceived',
  TRIGGER_NEW_INBOUND_FACEBOOK_MESSAGE_RECEIVED = 'newInboundFacebookMessageReceived',
  TRIGGER_NEW_INBOUND_INSTAGRAM_MESSAGE_RECEIVED = 'newInboundInstagramMessageReceived',
  TRIGGER_SHOPIFY_ORDER_CREATED = 'shopifyOrderCreated',
  TRIGGER_SHOPIFY_ORDER_PAID = 'shopifyOrderPaid',
  TRIGGER_SHOPIFY_ORDER_FULFILLED = 'shopifyOrderFulfilled',
  TRIGGER_SHOPIFY_ORDER_SHIPPED = 'shopifyOrderShipped',
  TRIGGER_SHOPIFY_ORDER_READY_FOR_PICKUP = 'shopifyOrderReadyForPickup',
  TRIGGER_SHOPIFY_ABANDONED_CHECKOUT = 'shopifyAbandonedCheckout',
  TRIGGER_WOOCOMMERCE_ORDER_CREATED = 'wooCommerceOrderCreated',
  TRIGGER_WOOCOMMERCE_ORDER_PAID = 'wooCommerceOrderPaid',
  TRIGGER_WOOCOMMERCE_ORDER_COMPLETED = 'wooCommerceOrderCompleted',
  TRIGGER_WOOCOMMERCE_ORDER_CANCELLED = 'wooCommerceOrderCancelled',
  TRIGGER_WOOCOMMERCE_ORDER_REFUNDED = 'wooCommerceOrderRefunded',
  TRIGGER_WOOCOMMERCE_ORDER_FAILED = 'wooCommerceOrderFailed',
  TRIGGER_WOOCOMMERCE_ABANDONED_CHECKOUT = 'wooCommerceAbandonedCheckout',
  // HubSpot triggers
  TRIGGER_HUBSPOT_CONTACT_PROPERTY_UPDATED = 'hubSpotContactPropertyChanged',
  TRIGGER_HUBSPOT_CONTACT_CREATED = 'hubSpotContactCreated',
  TRIGGER_HUBSPOT_DEAL_CREATED = 'hubSpotDealCreated',
  TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED = 'hubSpotDealPropertyChanged',
  TRIGGER_HUBSPOT_TICKET_CREATED = 'hubSpotTicketCreated',
  TRIGGER_HUBSPOT_TICKET_PROPERTY_UPDATED = 'hubSpotTicketPropertyChanged',
  TRIGGER_HUBSPOT_TASK_CREATED = 'hubSpotTaskCreated',
  TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED = 'hubSpotTaskPropertyChanged',
  TRIGGER_HUBSPOT_ORDER_CREATED = 'hubSpotOrderCreated',
  TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED = 'hubSpotOrderPropertyChanged',
  TRIGGER_HUBSPOT_MEETING_CREATED = 'hubSpotMeetingCreated',
  TRIGGER_HUBSPOT_MEETING_PROPERTY_UPDATED = 'hubSpotMeetingPropertyChanged',
  TRIGGER_HUBSPOT_CONVERSATION_CREATED = 'hubSpotConversationCreated',
  TRIGGER_HUBSPOT_CONVERSATION_PROPERTY_UPDATED = 'hubSpotConversationPropertyChanged',
  TRIGGER_HUBSPOT_CONVERSATION_MESSAGE = 'hubSpotConversationMessage',
  // Zoho triggers
  TRIGGER_ZOHO_CONTACT_PROPERTY_UPDATED = 'zohoCrmContactPropertyChanged',
  TRIGGER_ZOHO_CONTACT_CREATED = 'zohoCrmContactCreated',
  // Actions
  ACTION_SEND_SMS = 'sendSms',
  ACTION_SEND_EMAIL = 'sendEmail',
  ACTION_SEND_EMAIL_REPLY = 'sendEmailReply',
  ACTION_UPDATE_CONTACT = 'updateContact',
  ACTION_ADD_TAG = 'addTagToContact',
  ACTION_REMOVE_TAG = 'removeTagsFromContact',
  ACTION_ADD_TO_LIST = 'addListsToContact',
  ACTION_REMOVE_FROM_LIST = 'removeListsFromContact',
  ACTION_UNSUBSCRIBE_CONTACT = 'unsubscribeContact',
  ACTION_CREATE_NEW_TASK = 'createTask',
  ACTION_CREATE_NEW_DEAL = 'createDeal',
  ACTION_SEND_WEBHOOK = 'sendWebhook',
  ACTION_SEND_WHATSAPP_REPLY = 'sendWhatsappReply',
  ACTION_SEND_FACEBOOK_REPLY = 'sendFacebookReply',
  ACTION_SEND_INSTAGRAM_REPLY = 'sendInstagramReply',
  ACTION_STOP_ANOTHER_FLOW = 'stopAnotherAutomations',
  ACTION_SHOPIFY_ADD_CUSTOMER_TAG = 'shopifyAddCustomerTag',
  ACTION_SHOPIFY_ADD_CUSTOMER_NOTE = 'shopifyAddCustomerNote',
  ACTION_SHOPIFY_ADD_ORDER_TAG = 'shopifyAddOrderTag',
  ACTION_SHOPIFY_ADD_ORDER_NOTE = 'shopifyAddOrderNote',
  ACTION_WOOCOMMERCE_ADD_ORDER_NOTE = 'wooCommerceAddOrderNote',
  ACTION_WOOCOMMERCE_ADD_ORDER_TAG = 'wooCommerceAddOrderTag',
  ACTION_WOOCOMMERCE_UPDATE_ORDER_STATUS = 'wooCommerceUpdateOrderStatus',
  ACTION_WOOCOMMERCE_UPDATE_CUSTOMER_CUSTOM_FIELD = 'wooCommerceUpdateCustomerCustomField',
  ACTION_WOOCOMMERCE_UPDATE_ORDER_CUSTOM_FIELD = 'wooCommerceUpdateOrderCustomField',
  ACTION_WOOCOMMERCE_CREATE_COUPON = 'wooCommerceCreateCoupon',
  // HubSpot actions
  ACTION_HUBSPOT_UPDATE_CONTACT = 'hubSpotUpdateContact',
  ACTION_HUBSPOT_ADD_NOTE_TO_CONTACT = 'hubSpotAddNoteToContact',
  ACTION_HUBSPOT_ADD_NOTE_TO_DEAL = 'hubSpotAddNoteToDeal',
  ACTION_HUBSPOT_CREATE_TASK = 'hubSpotCreateTask',
  ACTION_HUBSPOT_CREATE_TICKET = 'hubSpotCreateTicket',
  ACTION_HUBSPOT_CREATE_DEAL = 'hubSpotCreateDeal',
  ACTION_HUBSPOT_MOVE_DEAL_TO_STAGE = 'hubSpotMoveDealToStage',
  ACTION_HUBSPOT_ADD_NOTE_TO_ORDER = 'hubSpotAddNoteToOrder',
  ACTION_HUBSPOT_UPDATE_ORDER_PROPERTY = 'hubSpotUpdateOrderProperty',
  ACTION_HUBSPOT_MOVE_ORDER_TO_STAGE = 'hubSpotMoveOrderToStage',
  ACTION_HUBSPOT_UPDATE_TASK_STATUS = 'hubSpotUpdateTaskStatus',
  ACTION_HUBSPOT_ADD_CONTACT_TO_LIST = 'hubSpotAddContactToList',
  ACTION_HUBSPOT_REMOVE_CONTACT_FROM_LIST = 'hubSpotRemoveContactFromList',
  // Rules
  RULE_DELAY = 'timeDelay',
  RULE_ADVANCED_TIME_DELAY = 'advancedTimeDelay',
  RULE_CONDITIONAL_SPLIT = 'conditionalSplit',
  RULE_CONDITIONAL_SPLIT_BRANCH = 'conditionalSplitBranch',
  RULE_PERCENTAGE_SPLIT = 'percentageSplit',
  RULE_PERCENTAGE_SPLIT_BRANCH = 'percentageSplitBranch',
  RULE_WAIT_UNTIL_EVENT_HAPPENS = 'waitUntilEventHappens',
  RULE_WAIT_UNTIL_EVENT_HAPPENS_BRANCH = 'waitUntilEventHappensSplitBranch',
  RULE_REPLY_BASED_SPLIT = 'replyBasedSplit',
  RULE_REPLY_BASED_SPLIT_BRANCH = 'replyBasedSplitBranch',
  RULE_GO_TO_ANOTHER_STEP = 'goToAnotherStep',
  RULE_GO_TO_ANOTHER_STEP_BRANCH = 'goAnotherStepBranch',
  // System
  SYSTEM_CONTACT_EXITS = 'contactExits',
}

export type AutomationStepGroup =
  | 'rules'
  | 'tmTriggers'
  | 'tmActions'
  | 'shopifyTriggers'
  | 'shopifyActions'
  | 'wooCommerceTriggers'
  | 'wooCommerceActions'
  | 'hubspotTriggers'
  | 'hubspotActions'
  | 'zohoTriggers'

const automationStepTypeToGroupRecord = {
  [AutomationStepType.TRIGGER_TAG_ADDED_TO_CONTACT]: 'tmTriggers',
  [AutomationStepType.TRIGGER_CONTACT_ADDED_TO_LIST]: 'tmTriggers',
  [AutomationStepType.TRIGGER_CONTACT_CREATED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_CONTACT_UPDATED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_SUBSCRIBE_FORM_FILLED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_NEW_INBOUND_SMS_RECEIVED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_NEW_INBOUND_EMAIL_RECEIVED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_MANUALLY_SELECT_CONTACTS]: 'tmTriggers',
  [AutomationStepType.TRIGGER_NEW_INBOUND_LIVE_CHAT_MESSAGE_RECEIVED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_NEW_INBOUND_WHATSAPP_MESSAGE_RECEIVED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_NEW_INBOUND_FACEBOOK_MESSAGE_RECEIVED]: 'tmTriggers',
  [AutomationStepType.TRIGGER_NEW_INBOUND_INSTAGRAM_MESSAGE_RECEIVED]: 'tmTriggers',
  [AutomationStepType.ACTION_SEND_SMS]: 'tmActions',
  [AutomationStepType.ACTION_SEND_EMAIL]: 'tmActions',
  [AutomationStepType.ACTION_SEND_EMAIL_REPLY]: 'tmActions',
  [AutomationStepType.ACTION_SEND_WHATSAPP_REPLY]: 'tmActions',
  [AutomationStepType.ACTION_SEND_FACEBOOK_REPLY]: 'tmActions',
  [AutomationStepType.ACTION_SEND_INSTAGRAM_REPLY]: 'tmActions',
  [AutomationStepType.ACTION_STOP_ANOTHER_FLOW]: 'tmActions',
  [AutomationStepType.ACTION_UPDATE_CONTACT]: 'tmActions',
  [AutomationStepType.ACTION_ADD_TAG]: 'tmActions',
  [AutomationStepType.ACTION_REMOVE_TAG]: 'tmActions',
  [AutomationStepType.ACTION_ADD_TO_LIST]: 'tmActions',
  [AutomationStepType.ACTION_REMOVE_FROM_LIST]: 'tmActions',
  [AutomationStepType.ACTION_UNSUBSCRIBE_CONTACT]: 'tmActions',
  [AutomationStepType.ACTION_CREATE_NEW_TASK]: 'tmActions',
  [AutomationStepType.ACTION_CREATE_NEW_DEAL]: 'tmActions',
  [AutomationStepType.ACTION_SEND_WEBHOOK]: 'tmActions',
  [AutomationStepType.RULE_DELAY]: 'rules',
  [AutomationStepType.RULE_ADVANCED_TIME_DELAY]: 'rules',
  [AutomationStepType.RULE_CONDITIONAL_SPLIT]: 'rules',
  [AutomationStepType.RULE_CONDITIONAL_SPLIT_BRANCH]: 'rules',
  [AutomationStepType.RULE_PERCENTAGE_SPLIT]: 'rules',
  [AutomationStepType.RULE_PERCENTAGE_SPLIT_BRANCH]: 'rules',
  [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS]: 'rules',
  [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS_BRANCH]: 'rules',
  [AutomationStepType.RULE_REPLY_BASED_SPLIT]: 'rules',
  [AutomationStepType.RULE_REPLY_BASED_SPLIT_BRANCH]: 'rules',
  [AutomationStepType.RULE_GO_TO_ANOTHER_STEP]: 'rules',
  [AutomationStepType.RULE_GO_TO_ANOTHER_STEP_BRANCH]: 'rules',
  [AutomationStepType.TRIGGER_SHOPIFY_ORDER_CREATED]: 'shopifyTriggers',
  [AutomationStepType.TRIGGER_SHOPIFY_ORDER_PAID]: 'shopifyTriggers',
  [AutomationStepType.TRIGGER_SHOPIFY_ORDER_FULFILLED]: 'shopifyTriggers',
  [AutomationStepType.TRIGGER_SHOPIFY_ORDER_SHIPPED]: 'shopifyTriggers',
  [AutomationStepType.TRIGGER_SHOPIFY_ORDER_READY_FOR_PICKUP]: 'shopifyTriggers',
  [AutomationStepType.TRIGGER_SHOPIFY_ABANDONED_CHECKOUT]: 'shopifyTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CREATED]: 'wooCommerceTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_PAID]: 'wooCommerceTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_COMPLETED]: 'wooCommerceTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CANCELLED]: 'wooCommerceTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_REFUNDED]: 'wooCommerceTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_FAILED]: 'wooCommerceTriggers',
  [AutomationStepType.TRIGGER_WOOCOMMERCE_ABANDONED_CHECKOUT]: 'wooCommerceTriggers',
  [AutomationStepType.ACTION_SHOPIFY_ADD_CUSTOMER_TAG]: 'shopifyActions',
  [AutomationStepType.ACTION_SHOPIFY_ADD_CUSTOMER_NOTE]: 'shopifyActions',
  [AutomationStepType.ACTION_SHOPIFY_ADD_ORDER_TAG]: 'shopifyActions',
  [AutomationStepType.ACTION_SHOPIFY_ADD_ORDER_NOTE]: 'shopifyActions',
  [AutomationStepType.ACTION_WOOCOMMERCE_ADD_ORDER_TAG]: 'wooCommerceActions',
  [AutomationStepType.ACTION_WOOCOMMERCE_UPDATE_ORDER_STATUS]: 'wooCommerceActions',
  [AutomationStepType.ACTION_WOOCOMMERCE_CREATE_COUPON]: 'wooCommerceActions',
  [AutomationStepType.ACTION_WOOCOMMERCE_ADD_ORDER_NOTE]: 'wooCommerceActions',
  [AutomationStepType.ACTION_WOOCOMMERCE_UPDATE_ORDER_CUSTOM_FIELD]: 'wooCommerceActions',
  [AutomationStepType.ACTION_WOOCOMMERCE_UPDATE_CUSTOMER_CUSTOM_FIELD]: 'wooCommerceActions',
  [AutomationStepType.TRIGGER_HUBSPOT_CONTACT_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_CONTACT_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_DEAL_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_TICKET_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_TICKET_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_TASK_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_ORDER_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_MEETING_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_MEETING_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_CREATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_PROPERTY_UPDATED]: 'hubspotTriggers',
  [AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_MESSAGE]: 'hubspotTriggers',
  [AutomationStepType.ACTION_HUBSPOT_UPDATE_CONTACT]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_CONTACT]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_DEAL]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_CREATE_TASK]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_CREATE_TICKET]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_CREATE_DEAL]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_MOVE_DEAL_TO_STAGE]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_ORDER]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_UPDATE_ORDER_PROPERTY]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_MOVE_ORDER_TO_STAGE]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_UPDATE_TASK_STATUS]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_ADD_CONTACT_TO_LIST]: 'hubspotActions',
  [AutomationStepType.ACTION_HUBSPOT_REMOVE_CONTACT_FROM_LIST]: 'hubspotActions',
  [AutomationStepType.TRIGGER_ZOHO_CONTACT_PROPERTY_UPDATED]: 'zohoTriggers',
  [AutomationStepType.TRIGGER_ZOHO_CONTACT_CREATED]: 'zohoTriggers',
} as const satisfies Record<Exclude<AutomationStepType, AutomationStepType.SYSTEM_CONTACT_EXITS>, AutomationStepGroup>

export const AUTOMATION_STEP_TYPE_TO_GROUP_MAP: Map<AutomationStepType, AutomationStepGroup> = new Map(
  Object.entries(automationStepTypeToGroupRecord) as [AutomationStepType, AutomationStepGroup][],
)

export enum ShopifyCustomerNotFoundStrategy {
  CREATE_CUSTOMER = 'create_customer',
  SKIP = 'skip',
  STOP_FLOW = 'stop_flow',
}

// WooCommerce shares the same customer-not-found strategy values as Shopify.
// oxlint-disable-next-line no-redeclare -- the value and type aliases share one name on purpose
export const WooCommerceCustomerNotFoundStrategy = ShopifyCustomerNotFoundStrategy
export type WooCommerceCustomerNotFoundStrategy = ShopifyCustomerNotFoundStrategy

// HubSpot shares the same customer-not-found strategy values as Shopify.
// oxlint-disable-next-line no-redeclare
export const HubSpotCustomerNotFoundStrategy = ShopifyCustomerNotFoundStrategy
export type HubSpotCustomerNotFoundStrategy = ShopifyCustomerNotFoundStrategy

export enum WooCommerceOrderStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  ON_HOLD = 'on-hold',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  FAILED = 'failed',
  DRAFT = 'draft',
  CHECKOUT_DRAFT = 'checkout-draft',
}

export enum WooCommerceCouponDiscountType {
  PERCENT = 'percent',
  FIXED_CART = 'fixed_cart',
  FIXED_PRODUCT = 'fixed_product',
}

// Single key/value pair for WooCommerce custom-field update actions (order/customer).
export type CustomFieldUpdateItem = {
  key: string
  value: string
}

// Same as CustomFieldUpdateItem but with a JSONContent value for the in-form Tiptap editor.
// `id` is a client-only stable key used for list rendering and per-row editor ids; it is not persisted.
export type CustomFieldUpdateFormItem = {
  id: string
  key: string
  value: JSONContent
}

// Shopify API limit for customer.note and order.note
export const SHOPIFY_NOTE_MAX_LENGTH = 5000

export const SHOPIFY_ORDER_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set([
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_CREATED,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_PAID,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_FULFILLED,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_SHIPPED,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_READY_FOR_PICKUP,
])

export const WOOCOMMERCE_ORDER_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set([
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CREATED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_PAID,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_COMPLETED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CANCELLED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_REFUNDED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_FAILED,
])

export enum AutomationWaitUntilEventHappensEventType {
  EMAIL_OPENED = 'emailOpened',
  LINK_IN_EMAIL_CLICKED = 'linkInEmailClicked',
  UNSUBSCRIBED_FROM_EMAILS = 'unsubscribedFromEmails',
  CONTACT_IS_ADDED_TO_LIST = 'contactIsAddedToList',
  CONTACT_REMOVED_FROM_LIST = 'contactRemovedFromList',
  TAG_IS_ADDED_TO_CONTACT = 'tagIsAddedToContact',
  TAG_REMOVED_FROM_CONTACT = 'tagRemovedFromContact',
  INBOUND_MESSAGE_COMPLEX_EVENT = 'newInboundMessageComplex',
  SHOPIFY_ORDER_CREATED = 'shopifyOrderCreated',
  SHOPIFY_ORDER_PAID = 'shopifyOrderPaid',
  SHOPIFY_ORDER_FULFILLED = 'shopifyOrderFulfilled',
  SHOPIFY_ORDER_SHIPPED = 'shopifyOrderShipped',
  SHOPIFY_ORDER_READY_FOR_PICKUP = 'shopifyOrderReadyForPickup',
  SHOPIFY_ABANDONED_CHECKOUT = 'shopifyAbandonedCheckout',
  WOOCOMMERCE_ORDER_CREATED = 'wooCommerceOrderCreated',
  WOOCOMMERCE_ORDER_PAID = 'wooCommerceOrderPaid',
  WOOCOMMERCE_ORDER_COMPLETED = 'wooCommerceOrderCompleted',
  WOOCOMMERCE_ORDER_CANCELLED = 'wooCommerceOrderCancelled',
  WOOCOMMERCE_ORDER_REFUNDED = 'wooCommerceOrderRefunded',
  WOOCOMMERCE_ORDER_FAILED = 'wooCommerceOrderFailed',
  WOOCOMMERCE_ABANDONED_CHECKOUT = 'wooCommerceAbandonedCheckout',
  HUBSPOT_CONTACT_CREATED = 'hubSpotContactCreated',
  HUBSPOT_CONTACT_PROPERTY_CHANGED = 'hubSpotContactPropertyChanged',
  HUBSPOT_DEAL_CREATED = 'hubSpotDealCreated',
  HUBSPOT_DEAL_PROPERTY_CHANGED = 'hubSpotDealPropertyChanged',
  HUBSPOT_TICKET_CREATED = 'hubSpotTicketCreated',
  HUBSPOT_TICKET_PROPERTY_CHANGED = 'hubSpotTicketPropertyChanged',
  HUBSPOT_TASK_CREATED = 'hubSpotTaskCreated',
  HUBSPOT_TASK_PROPERTY_CHANGED = 'hubSpotTaskPropertyChanged',
  HUBSPOT_ORDER_CREATED = 'hubSpotOrderCreated',
  HUBSPOT_ORDER_PROPERTY_CHANGED = 'hubSpotOrderPropertyChanged',
  HUBSPOT_MEETING_CREATED = 'hubSpotMeetingCreated',
  HUBSPOT_MEETING_PROPERTY_CHANGED = 'hubSpotMeetingPropertyChanged',
  HUBSPOT_CONVERSATION_CREATED = 'hubSpotConversationCreated',
  HUBSPOT_CONVERSATION_PROPERTY_CHANGED = 'hubSpotConversationPropertyChanged',
  HUBSPOT_CONVERSATION_MESSAGE = 'hubSpotConversationMessage',
}

export enum AutomationWaitUntilEventHappensLegacyMessageEventType {
  NEW_INBOUND_SMS = 'newInboundMessage',
  NEW_INBOUND_EMAIL = 'newInboundEmail',
  NEW_INBOUND_LIVE_CHAT_MESSAGE = 'newInboundLiveChatMessage',
  NEW_INBOUND_WHATSAPP_MESSAGE = 'newInboundWhatsappMessage',
  NEW_INBOUND_FACEBOOK_MESSAGE = 'newInboundFacebookMessage',
  NEW_INBOUND_INSTAGRAM_MESSAGE = 'newInboundInstagramMessage',
}

export const isAutomationWaitUntilEventHappensEventType = (
  eventType: unknown,
): eventType is AutomationWaitUntilEventHappensEventType => {
  return Object.values(AutomationWaitUntilEventHappensEventType).includes(
    eventType as AutomationWaitUntilEventHappensEventType,
  )
}

export type AutomationWaitUntilEventHappensShopifyEventBasePayload = {
  connectionId: number
  productIds: string[]
  categoryIds: string[]
  orderValue?: ShopifyOrderValueFilter
}

export type AutomationWaitUntilEventHappensWooCommerceEventBasePayload = {
  connectionId: number
  productIds: string[]
  categoryIds: string[]
  orderValue?: ShopifyOrderValueFilter
}

export const isAutomationWaitUntilEventHappensLegacyMessageEventType = (
  eventType: unknown,
): eventType is AutomationWaitUntilEventHappensLegacyMessageEventType => {
  return Object.values(AutomationWaitUntilEventHappensLegacyMessageEventType).includes(
    eventType as AutomationWaitUntilEventHappensLegacyMessageEventType,
  )
}

export enum AutomationWuehMessageComplexEventChannel {
  SMS = 'sms',
  EMAIL = 'email',
  LIVE_CHAT = 'liveChat',
  WHATSAPP = 'whatsapp',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
}

type AutomationWaitUntilEventHappensPayloadMap = Satisfy<
  Record<
    AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType,
    Record<string, unknown>
  >,
  {
    [AutomationWaitUntilEventHappensEventType.EMAIL_OPENED]: {
      sentEmailActionLocalId: number
    }
    [AutomationWaitUntilEventHappensEventType.LINK_IN_EMAIL_CLICKED]: {
      sentEmailActionLocalId: number
      links: string[]
    }
    [AutomationWaitUntilEventHappensEventType.UNSUBSCRIBED_FROM_EMAILS]: {}
    [AutomationWaitUntilEventHappensEventType.CONTACT_IS_ADDED_TO_LIST]: {
      oneOfGroupsIds: number[]
    }
    [AutomationWaitUntilEventHappensEventType.CONTACT_REMOVED_FROM_LIST]: {
      oneOfGroupsIds: number[]
    }
    [AutomationWaitUntilEventHappensEventType.TAG_IS_ADDED_TO_CONTACT]: {
      oneOfTagsIds: number[]
    }
    [AutomationWaitUntilEventHappensEventType.TAG_REMOVED_FROM_CONTACT]: {
      oneOfTagsIds: number[]
    }
    [AutomationWaitUntilEventHappensEventType.INBOUND_MESSAGE_COMPLEX_EVENT]: {
      [AutomationWuehMessageComplexEventChannel.SMS]: Nullable<AutomationWuehNewInboundSmsPayload>
      [AutomationWuehMessageComplexEventChannel.EMAIL]: Nullable<AutomationNewInboundEmailReceivedFilterPayload>
      [AutomationWuehMessageComplexEventChannel.LIVE_CHAT]: Nullable<AutomationNewInboundLiveChatMessagePayload>
      [AutomationWuehMessageComplexEventChannel.WHATSAPP]: Nullable<AutomationNewInboundWhatsappMessagePayload>
      [AutomationWuehMessageComplexEventChannel.FACEBOOK]: Nullable<AutomationNewInboundFacebookMessagePayload>
      [AutomationWuehMessageComplexEventChannel.INSTAGRAM]: Nullable<AutomationNewInboundInstagramMessagePayload>
    }
    [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_CREATED]: AutomationWaitUntilEventHappensShopifyEventBasePayload
    [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_PAID]: AutomationWaitUntilEventHappensShopifyEventBasePayload
    [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_FULFILLED]: AutomationWaitUntilEventHappensShopifyEventBasePayload
    [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_SHIPPED]: AutomationWaitUntilEventHappensShopifyEventBasePayload
    [AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_READY_FOR_PICKUP]: AutomationWaitUntilEventHappensShopifyEventBasePayload
    [AutomationWaitUntilEventHappensEventType.SHOPIFY_ABANDONED_CHECKOUT]: AutomationWaitUntilEventHappensShopifyEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CREATED]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_PAID]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_COMPLETED]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CANCELLED]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_REFUNDED]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_FAILED]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ABANDONED_CHECKOUT]: AutomationWaitUntilEventHappensWooCommerceEventBasePayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_CREATED]: AutomationFlowHubspotContactCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_PROPERTY_CHANGED]: AutomationFlowHubspotContactPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_CREATED]: AutomationFlowHubspotDealCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_PROPERTY_CHANGED]: AutomationFlowHubspotDealPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_CREATED]: AutomationFlowHubspotTicketCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_PROPERTY_CHANGED]: AutomationFlowHubspotTicketPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_CREATED]: AutomationFlowHubspotTaskCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_PROPERTY_CHANGED]: AutomationFlowHubspotTaskPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_CREATED]: AutomationFlowHubspotOrderCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_PROPERTY_CHANGED]: AutomationFlowHubspotOrderPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_CREATED]: AutomationFlowHubspotMeetingCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_PROPERTY_CHANGED]: AutomationFlowHubspotMeetingPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_CREATED]: AutomationFlowHubspotConversationCreatedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_PROPERTY_CHANGED]: AutomationFlowHubspotConversationPropertyChangedPayload
    [AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_MESSAGE]: AutomationFlowHubspotConversationMessagePayload
    // Legacy events
    [AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_SMS]: AutomationWuehNewInboundSmsPayload
    [AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_EMAIL]: AutomationNewInboundEmailReceivedFilterPayload
    [AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_LIVE_CHAT_MESSAGE]: AutomationNewInboundLiveChatMessagePayload
    [AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_WHATSAPP_MESSAGE]: AutomationNewInboundWhatsappMessagePayload
    [AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_FACEBOOK_MESSAGE]: AutomationNewInboundFacebookMessagePayload
    [AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_INSTAGRAM_MESSAGE]: AutomationNewInboundInstagramMessagePayload
  }
>

export type AutomationWaitUntilEventHappensPayloadMapPrepared = {
  [P in keyof AutomationWaitUntilEventHappensPayloadMap]: { type: P } & AutomationWaitUntilEventHappensPayloadMap[P]
}

export type AutomationWaitUntilEventHappensPayload<
  Type extends AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType =
    | AutomationWaitUntilEventHappensEventType
    | AutomationWaitUntilEventHappensLegacyMessageEventType,
> = AutomationWaitUntilEventHappensPayloadMapPrepared[Type]

export const isAutomationWaitUntilEventHappensLegacyMessageEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensPayload<AutomationWaitUntilEventHappensLegacyMessageEventType> => {
  return (
    isRecordUnknown(payload) &&
    'type' in payload &&
    isAutomationWaitUntilEventHappensLegacyMessageEventType(payload.type)
  )
}

export const WUEH_LEGACY_MESSAGE_EVENT_TYPE_BY_CHANNEL: Record<
  AutomationWuehMessageComplexEventChannel,
  AutomationWaitUntilEventHappensLegacyMessageEventType
> = {
  [AutomationWuehMessageComplexEventChannel.SMS]: AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_SMS,
  [AutomationWuehMessageComplexEventChannel.EMAIL]:
    AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_EMAIL,
  [AutomationWuehMessageComplexEventChannel.LIVE_CHAT]:
    AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_LIVE_CHAT_MESSAGE,
  [AutomationWuehMessageComplexEventChannel.WHATSAPP]:
    AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_WHATSAPP_MESSAGE,
  [AutomationWuehMessageComplexEventChannel.FACEBOOK]:
    AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_FACEBOOK_MESSAGE,
  [AutomationWuehMessageComplexEventChannel.INSTAGRAM]:
    AutomationWaitUntilEventHappensLegacyMessageEventType.NEW_INBOUND_INSTAGRAM_MESSAGE,
}

// Covers both formats of a WUEH inbound message event: the multi-channel one and the legacy per-channel ones
export const isWuehInboundMessageEventForChannel = (
  event: AutomationWaitUntilEventHappensPayload,
  channel: AutomationWuehMessageComplexEventChannel,
): boolean => {
  if (event.type === AutomationWaitUntilEventHappensEventType.INBOUND_MESSAGE_COMPLEX_EVENT) {
    return !isNil(event[channel])
  }

  return event.type === WUEH_LEGACY_MESSAGE_EVENT_TYPE_BY_CHANNEL[channel]
}

// email event types
export const automationWaitUntilEventHappensEmailEventTypes = [
  AutomationWaitUntilEventHappensEventType.EMAIL_OPENED,
  AutomationWaitUntilEventHappensEventType.LINK_IN_EMAIL_CLICKED,
] as const
export type AutomationWaitUntilEventHappensEmailEventPayload = AutomationWaitUntilEventHappensPayload<
  (typeof automationWaitUntilEventHappensEmailEventTypes)[number]
>
export const isAutomationWaitUntilEventHappensEmailEventType = (
  eventType: unknown,
): eventType is (typeof automationWaitUntilEventHappensEmailEventTypes)[number] => {
  return automationWaitUntilEventHappensEmailEventTypes.includes(
    eventType as (typeof automationWaitUntilEventHappensEmailEventTypes)[number],
  )
}
export const isAutomationWaitUntilEventHappensEmailEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensEmailEventPayload => {
  return isRecordUnknown(payload) && 'type' in payload && isAutomationWaitUntilEventHappensEmailEventType(payload.type)
}

// contact list event types
export const automationWaitUntilEventHappensContactListEventTypes = [
  AutomationWaitUntilEventHappensEventType.CONTACT_IS_ADDED_TO_LIST,
  AutomationWaitUntilEventHappensEventType.CONTACT_REMOVED_FROM_LIST,
] as const
export type AutomationWaitUntilEventHappensContactListEventPayload = AutomationWaitUntilEventHappensPayload<
  (typeof automationWaitUntilEventHappensContactListEventTypes)[number]
>
export const isAutomationWaitUntilEventHappensContactListEventType = (
  eventType: unknown,
): eventType is (typeof automationWaitUntilEventHappensContactListEventTypes)[number] => {
  return automationWaitUntilEventHappensContactListEventTypes.includes(
    eventType as (typeof automationWaitUntilEventHappensContactListEventTypes)[number],
  )
}
export const isAutomationWaitUntilEventHappensContactListEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensContactListEventPayload => {
  return (
    isRecordUnknown(payload) && 'type' in payload && isAutomationWaitUntilEventHappensContactListEventType(payload.type)
  )
}

// `sorting` of the "happened" (yes) branch of a WUEH step.
// Set in AutomationRuleWaitUntilEventHappensService.addStepToFlow as index + 1 (first branch = happened).
export const WUEH_HAPPENED_BRANCH_SORTING = 1

// Shopify WUEH events that produce a "recent order" context (excluding abandoned checkout, which has no order yet)
export const SHOPIFY_ORDER_WUEH_EVENT_TYPES: ReadonlySet<
  AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType
> = new Set([
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_PAID,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_FULFILLED,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_SHIPPED,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_READY_FOR_PICKUP,
])

// WooCommerce WUEH events that produce a "recent order" context (excluding abandoned checkout, which has no order yet)
export const WOOCOMMERCE_ORDER_WUEH_EVENT_TYPES: ReadonlySet<
  AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType
> = new Set([
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_PAID,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_COMPLETED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CANCELLED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_REFUNDED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_FAILED,
])

// shopify event types
export const automationWaitUntilEventHappensShopifyEventTypes = [
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_PAID,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_FULFILLED,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_SHIPPED,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ORDER_READY_FOR_PICKUP,
  AutomationWaitUntilEventHappensEventType.SHOPIFY_ABANDONED_CHECKOUT,
] as const

export type AutomationWaitUntilEventHappensShopifyEventPayload = AutomationWaitUntilEventHappensPayload<
  (typeof automationWaitUntilEventHappensShopifyEventTypes)[number]
>

export const isAutomationWaitUntilEventHappensShopifyEventType = (
  eventType: unknown,
): eventType is (typeof automationWaitUntilEventHappensShopifyEventTypes)[number] => {
  return automationWaitUntilEventHappensShopifyEventTypes.includes(
    eventType as (typeof automationWaitUntilEventHappensShopifyEventTypes)[number],
  )
}

export const isAutomationWaitUntilEventHappensShopifyEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensShopifyEventPayload => {
  return (
    isRecordUnknown(payload) && 'type' in payload && isAutomationWaitUntilEventHappensShopifyEventType(payload.type)
  )
}

// woocommerce event types
export const automationWaitUntilEventHappensWooCommerceEventTypes = [
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_PAID,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_COMPLETED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_CANCELLED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_REFUNDED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ORDER_FAILED,
  AutomationWaitUntilEventHappensEventType.WOOCOMMERCE_ABANDONED_CHECKOUT,
] as const

export type AutomationWaitUntilEventHappensWooCommerceEventPayload = AutomationWaitUntilEventHappensPayload<
  (typeof automationWaitUntilEventHappensWooCommerceEventTypes)[number]
>

export const isAutomationWaitUntilEventHappensWooCommerceEventType = (
  eventType: unknown,
): eventType is (typeof automationWaitUntilEventHappensWooCommerceEventTypes)[number] => {
  return automationWaitUntilEventHappensWooCommerceEventTypes.includes(
    eventType as (typeof automationWaitUntilEventHappensWooCommerceEventTypes)[number],
  )
}

export const isAutomationWaitUntilEventHappensWooCommerceEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensWooCommerceEventPayload => {
  return (
    isRecordUnknown(payload) && 'type' in payload && isAutomationWaitUntilEventHappensWooCommerceEventType(payload.type)
  )
}

// hubspot event types
export const automationWaitUntilEventHappensHubspotEventTypes = [
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_MESSAGE,
] as const

export type AutomationWaitUntilEventHappensHubspotEventType =
  (typeof automationWaitUntilEventHappensHubspotEventTypes)[number]

export type AutomationWaitUntilEventHappensHubspotEventPayload =
  AutomationWaitUntilEventHappensPayload<AutomationWaitUntilEventHappensHubspotEventType>

export const isAutomationWaitUntilEventHappensHubspotEventType = (
  eventType: unknown,
): eventType is (typeof automationWaitUntilEventHappensHubspotEventTypes)[number] => {
  return automationWaitUntilEventHappensHubspotEventTypes.includes(
    eventType as (typeof automationWaitUntilEventHappensHubspotEventTypes)[number],
  )
}

export const isAutomationWaitUntilEventHappensHubspotEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensHubspotEventPayload => {
  return (
    isRecordUnknown(payload) && 'type' in payload && isAutomationWaitUntilEventHappensHubspotEventType(payload.type)
  )
}

export const automationWaitUntilEventHappensHubspotPropertyChangedEventTypes = [
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_PROPERTY_CHANGED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_PROPERTY_CHANGED,
] as const

export type AutomationWaitUntilEventHappensHubspotPropertyChangedEventType =
  (typeof automationWaitUntilEventHappensHubspotPropertyChangedEventTypes)[number]

export const isAutomationWaitUntilEventHappensHubspotPropertyChangedEventType = (
  eventType: unknown,
): eventType is AutomationWaitUntilEventHappensHubspotPropertyChangedEventType => {
  return automationWaitUntilEventHappensHubspotPropertyChangedEventTypes.includes(
    eventType as AutomationWaitUntilEventHappensHubspotPropertyChangedEventType,
  )
}

export const automationWaitUntilEventHappensHubspotCreatedEventTypes = [
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONTACT_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TICKET_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_MEETING_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_CONVERSATION_MESSAGE,
] as const

export type AutomationWaitUntilEventHappensHubspotCreatedEventType =
  (typeof automationWaitUntilEventHappensHubspotCreatedEventTypes)[number]

export const isAutomationWaitUntilEventHappensHubspotCreatedEventType = (
  eventType: unknown,
): eventType is AutomationWaitUntilEventHappensHubspotCreatedEventType => {
  return automationWaitUntilEventHappensHubspotCreatedEventTypes.includes(
    eventType as AutomationWaitUntilEventHappensHubspotCreatedEventType,
  )
}

// contact tag event types
export const automationWaitUntilEventHappensContactTagEventTypes = [
  AutomationWaitUntilEventHappensEventType.TAG_IS_ADDED_TO_CONTACT,
  AutomationWaitUntilEventHappensEventType.TAG_REMOVED_FROM_CONTACT,
] as const
export type AutomationWaitUntilEventHappensContactTagEventPayload = AutomationWaitUntilEventHappensPayload<
  (typeof automationWaitUntilEventHappensContactTagEventTypes)[number]
>
export const isAutomationWaitUntilEventHappensContactTagEventType = (
  eventType: unknown,
): eventType is (typeof automationWaitUntilEventHappensContactTagEventTypes)[number] => {
  return automationWaitUntilEventHappensContactTagEventTypes.includes(
    eventType as (typeof automationWaitUntilEventHappensContactTagEventTypes)[number],
  )
}
export const isAutomationWaitUntilEventHappensContactTagEventPayload = (
  payload: unknown,
): payload is AutomationWaitUntilEventHappensContactTagEventPayload => {
  return (
    isRecordUnknown(payload) && 'type' in payload && isAutomationWaitUntilEventHappensContactTagEventType(payload.type)
  )
}

interface AutomationDayPeriod {
  /**
   * Format HH:mm (DATE_FORMATES.DATEPICKER_TIME_FORMAT)
   */
  from: string
  /**
   * Format HH:mm (DATE_FORMATES.DATEPICKER_TIME_FORMAT)
   */
  to: string
}
interface AutomationDaySchedule {
  day: WeekDaysNumber
  schedule: AutomationDayPeriod[]
}

// @todo rename
export interface AutomationActionSendTime {
  timezoneId: number
  timeParams: AutomationDaySchedule[]
}

export enum AutomationReplyBasedSplitType {
  SMS = 'sms',
  EMAIL = 'email',
}

export type AutomationNewInboundSmsReceivedFilterPayload = {
  phones?: {
    phones: string[]
  }
  content?: INewInboundMessageReceivedContentFilter
  country?: {
    in: string[]
  }
}

export type AutomationReplyBasedSplitBranchSmsCondition = Omit<
  AutomationNewInboundSmsReceivedFilterPayload,
  'content'
> & {
  type: AutomationReplyBasedSplitType.SMS
  contentFilter?: INewInboundMessageReceivedContentFilter
}

export type AutomationNewInboundEmailReceivedFilterPayload = {
  ticketMessageFilters: [
    {
      id?: Nullable<PaginationUrlFilterScalarValueType>
      ticket?: Nullable<PaginationUrlFilterSingleType>
      message?: Nullable<PaginationUrlFilterScalarValueType>
    },
  ]
}

export type AutomationReplyBasedSplitBranchEmailCondition = AutomationNewInboundEmailReceivedFilterPayload & {
  type: AutomationReplyBasedSplitType.EMAIL
}

export type AutomationReplyBasedSplitAnyReplyCondition = {
  type: AutomationReplyBasedSplitType
}
export type AutomationReplyBasedSplitNoReplyCondition = null

export type AutomationReplyBasedSplitBranchCondition =
  | AutomationReplyBasedSplitBranchSmsCondition
  | AutomationReplyBasedSplitBranchEmailCondition
  | AutomationReplyBasedSplitAnyReplyCondition
  | AutomationReplyBasedSplitNoReplyCondition

export const isAutomationReplyBasedSplitBranchSmsCondition = (
  condition: AutomationReplyBasedSplitBranchCondition,
): condition is AutomationReplyBasedSplitBranchSmsCondition => {
  return (
    isRecordUnknown(condition) &&
    condition.type === AutomationReplyBasedSplitType.SMS &&
    ('phones' in condition || 'contentFilter' in condition || 'country' in condition)
  )
}

export const isAutomationReplyBasedSplitBranchEmailCondition = (
  condition: AutomationReplyBasedSplitBranchCondition,
): condition is AutomationReplyBasedSplitBranchEmailCondition => {
  return (
    isRecordUnknown(condition) &&
    condition.type === AutomationReplyBasedSplitType.EMAIL &&
    'ticketMessageFilters' in condition
  )
}

export type AutomationFlowStepPayloadMap = Satisfy<
  Record<AutomationStepType, Record<string, unknown>>,
  {
    // Triggers
    [AutomationStepType.TRIGGER_TAG_ADDED_TO_CONTACT]: AutomationStepPayloadWithAdditionalFilters & {
      oneOfTagsIds: string[]
    }
    [AutomationStepType.TRIGGER_CONTACT_ADDED_TO_LIST]: AutomationStepPayloadWithAdditionalFilters & {
      oneOfGroupsIds: string[]
    }
    [AutomationStepType.TRIGGER_CONTACT_CREATED]: AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.TRIGGER_CONTACT_UPDATED]: {
      watchedFields: AutomationContactWatchedFields | null
    }
    [AutomationStepType.TRIGGER_SUBSCRIBE_FORM_FILLED]: AutomationStepPayloadWithAdditionalFilters & {
      oneOfFormIds: string[]
    }
    [AutomationStepType.TRIGGER_NEW_INBOUND_SMS_RECEIVED]: {
      phones?: {
        phones: string[]
      }
      country?: {
        in: TmCountryCode[]
      }
      contentFilter?: INewInboundMessageReceivedContentFilter
    } & AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.TRIGGER_NEW_INBOUND_EMAIL_RECEIVED]: AutomationNewInboundEmailReceivedFilterPayload &
      AutomationStepPayloadWithAdditionalFilters & {
        isOncePerTicket: boolean
      }
    [AutomationStepType.TRIGGER_NEW_INBOUND_LIVE_CHAT_MESSAGE_RECEIVED]: AutomationNewInboundLiveChatMessagePayload &
      AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.TRIGGER_NEW_INBOUND_WHATSAPP_MESSAGE_RECEIVED]: AutomationNewInboundWhatsappMessagePayload &
      AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.TRIGGER_NEW_INBOUND_FACEBOOK_MESSAGE_RECEIVED]: AutomationNewInboundFacebookMessagePayload &
      AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.TRIGGER_NEW_INBOUND_INSTAGRAM_MESSAGE_RECEIVED]: AutomationNewInboundInstagramMessagePayload &
      AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.TRIGGER_MANUALLY_SELECT_CONTACTS]: {
      contactsIds: number[]
      groupsIds: number[]
      segmentsIds: number[]
      totalContactsCount?: number // in response from BE only
    }
    [AutomationStepType.TRIGGER_SHOPIFY_ORDER_CREATED]: AutomationFlowShopifyTriggerBasePayload
    [AutomationStepType.TRIGGER_SHOPIFY_ORDER_PAID]: AutomationFlowShopifyTriggerBasePayload
    [AutomationStepType.TRIGGER_SHOPIFY_ORDER_SHIPPED]: AutomationFlowShopifyTriggerBasePayload
    [AutomationStepType.TRIGGER_SHOPIFY_ORDER_FULFILLED]: AutomationFlowShopifyTriggerBasePayload
    [AutomationStepType.TRIGGER_SHOPIFY_ORDER_READY_FOR_PICKUP]: AutomationFlowShopifyTriggerBasePayload
    [AutomationStepType.TRIGGER_SHOPIFY_ABANDONED_CHECKOUT]: AutomationFlowShopifyTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CREATED]: AutomationFlowWooCommerceTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_PAID]: AutomationFlowWooCommerceTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_COMPLETED]: AutomationFlowWooCommerceTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CANCELLED]: AutomationFlowWooCommerceTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_REFUNDED]: AutomationFlowWooCommerceTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_FAILED]: AutomationFlowWooCommerceTriggerBasePayload
    [AutomationStepType.TRIGGER_WOOCOMMERCE_ABANDONED_CHECKOUT]: AutomationFlowWooCommerceTriggerBasePayload
    // HubSpot triggers
    [AutomationStepType.TRIGGER_HUBSPOT_CONTACT_PROPERTY_UPDATED]: AutomationFlowHubspotContactPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_CONTACT_CREATED]: AutomationFlowHubspotContactCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_DEAL_CREATED]: AutomationFlowHubspotDealCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED]: AutomationFlowHubspotDealPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_TICKET_CREATED]: AutomationFlowHubspotTicketCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_TICKET_PROPERTY_UPDATED]: AutomationFlowHubspotTicketPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_TASK_CREATED]: AutomationFlowHubspotTaskCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED]: AutomationFlowHubspotTaskPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_ORDER_CREATED]: AutomationFlowHubspotOrderCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED]: AutomationFlowHubspotOrderPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_MEETING_CREATED]: AutomationFlowHubspotMeetingCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_MEETING_PROPERTY_UPDATED]: AutomationFlowHubspotMeetingPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_CREATED]: AutomationFlowHubspotConversationCreatedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_PROPERTY_UPDATED]: AutomationFlowHubspotConversationPropertyChangedPayload
    [AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_MESSAGE]: AutomationFlowHubspotConversationMessagePayload
    // Zoho triggers
    [AutomationStepType.TRIGGER_ZOHO_CONTACT_PROPERTY_UPDATED]: AutomationFlowZohoContactPropertyChangedPayload
    [AutomationStepType.TRIGGER_ZOHO_CONTACT_CREATED]: AutomationFlowZohoContactCreatedPayload
    // Actions
    [AutomationStepType.ACTION_SEND_SMS]: {
      text: Nullable<string>
      sendTime?: Nullable<AutomationActionSendTime>
      sender: Nullable<AutomationFlowSendSmsSender>
      labelId: Nullable<string>
    }
    [AutomationStepType.ACTION_SEND_EMAIL]: {
      emailSenderId: Nullable<number>
      subject: Nullable<string>
      message: Nullable<string>
      sendTime?: Nullable<AutomationActionSendTime>
      fromName: Nullable<string>
      replyToEmail: Nullable<string>
      fileIds: string[]
      labelId: Nullable<string>
    }
    [AutomationStepType.ACTION_SEND_EMAIL_REPLY]: {
      submitStatusId: Nullable<number | `${number}`>
      message: Nullable<string>
      fileIds: string[]
    }
    [AutomationStepType.ACTION_SEND_WHATSAPP_REPLY]: AutomationChannelReplyActionPayload
    [AutomationStepType.ACTION_SEND_FACEBOOK_REPLY]: AutomationChannelReplyActionPayload
    [AutomationStepType.ACTION_SEND_INSTAGRAM_REPLY]: AutomationChannelReplyActionPayload
    [AutomationStepType.ACTION_STOP_ANOTHER_FLOW]: {
      automationIds: number[]
    }
    [AutomationStepType.ACTION_UPDATE_CONTACT]: {
      companyName: Nullable<PatchPayload<string>>
      ownerId: Nullable<PatchPayload<number | `${number}`>>
      customFields: PatchPayload<CustomFieldUpdateFormBody[], true>
    }
    [AutomationStepType.ACTION_ADD_TAG]: {
      tagIds: string[]
    }
    [AutomationStepType.ACTION_REMOVE_TAG]: {
      tagIds: string[]
    }
    [AutomationStepType.ACTION_ADD_TO_LIST]: {
      groupIds: string[]
    }
    [AutomationStepType.ACTION_REMOVE_FROM_LIST]: {
      groupIds: string[]
    }
    [AutomationStepType.ACTION_UNSUBSCRIBE_CONTACT]: {
      sms: boolean
      email: boolean
    }
    [AutomationStepType.ACTION_CREATE_NEW_TASK]: {
      title: Nullable<string>
      description: Nullable<string>
      boardId: Nullable<string>
      stageId: Nullable<string>
      priority: Nullable<PriorityEnum>
      dueDateInterval: Nullable<AutomationDelay>
      assignee: Nullable<{
        strategy: 'explicit' | 'contact_owner'
        userId: Nullable<number | `${number}`>
      }>
    }
    [AutomationStepType.ACTION_CREATE_NEW_DEAL]: {
      title: Nullable<string>
      description: Nullable<string>
      pipelineId: Nullable<string>
      dealStageId: Nullable<string>
      amount: Nullable<number>
      assignee: Nullable<{
        strategy: 'explicit' | 'contact_owner'
        userId: Nullable<number | `${number}`>
      }>
      estimatedCloseDateInterval: Nullable<AutomationDelay>
      probability: Nullable<number>
    }
    [AutomationStepType.ACTION_SEND_WEBHOOK]: {
      method: Nullable<WebhookMethod>
      url: Nullable<string>
      bodyType: Nullable<WebhookBodyType>
      body: Nullable<string>
      queryParams: WebhookKeyValue[]
      headers: WebhookKeyValue[]
      auth: Nullable<WebhookAuth>
      signingSecretRef: Nullable<string>
      retry: boolean
      onFailure: Nullable<WebhookFailurePolicy>
    }
    [AutomationStepType.ACTION_SHOPIFY_ADD_CUSTOMER_TAG]: {
      tagIds: string[]
      customerNotFoundStrategy: ShopifyCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_SHOPIFY_ADD_CUSTOMER_NOTE]: {
      note: Nullable<string>
      customerNotFoundStrategy: ShopifyCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_SHOPIFY_ADD_ORDER_TAG]: {
      tagIds: string[]
    }
    [AutomationStepType.ACTION_SHOPIFY_ADD_ORDER_NOTE]: {
      note: Nullable<string>
    }
    [AutomationStepType.ACTION_WOOCOMMERCE_ADD_ORDER_NOTE]: {
      note: Nullable<string>
      isCustomerNote: boolean
    }
    [AutomationStepType.ACTION_WOOCOMMERCE_ADD_ORDER_TAG]: {
      tagIds: string[]
    }
    [AutomationStepType.ACTION_WOOCOMMERCE_UPDATE_ORDER_STATUS]: {
      status: Nullable<WooCommerceOrderStatus>
    }
    [AutomationStepType.ACTION_WOOCOMMERCE_UPDATE_CUSTOMER_CUSTOM_FIELD]: {
      fields: CustomFieldUpdateItem[]
      customerNotFoundStrategy: ShopifyCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_WOOCOMMERCE_UPDATE_ORDER_CUSTOM_FIELD]: {
      fields: CustomFieldUpdateItem[]
    }
    [AutomationStepType.ACTION_WOOCOMMERCE_CREATE_COUPON]: {
      discountType: Nullable<WooCommerceCouponDiscountType>
      amount: Nullable<string>
      expiresAfterDays: Nullable<number>
      usageLimit: Nullable<number>
      couponPrefix: Nullable<string>
      description: Nullable<string>
      productIds: string[]
    }
    // HubSpot actions
    [AutomationStepType.ACTION_HUBSPOT_UPDATE_CONTACT]: {
      fields: HubSpotFieldUpdateItem[]
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_CONTACT]: {
      note: Nullable<string>
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_DEAL]: {
      note: Nullable<string>
      // Optional: steps saved before this field existed have no value — read with a skip fallback.
      dealNotFoundStrategy?: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_CREATE_TASK]: {
      subject: Nullable<string>
      taskType: HubSpotTaskType
      owner: HubSpotTaskOwnerInstruction
      dueDate: Nullable<HubSpotTaskDueDate>
      priority: HubSpotTaskPriority
      body: Nullable<string>
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_CREATE_TICKET]: {
      subject: Nullable<string>
      pipelineId: Nullable<string>
      pipelineStageId: Nullable<string>
      priority: HubSpotTicketPriority
      ownerId: Nullable<string>
      description: Nullable<string>
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_CREATE_DEAL]: {
      dealName: Nullable<string>
      pipelineId: Nullable<string>
      dealStageId: Nullable<string>
      amount: Nullable<number>
      ownerId: Nullable<string>
      priority: HubSpotDealPriority
      closeDate: Nullable<HubSpotDealCloseDate>
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_MOVE_DEAL_TO_STAGE]: {
      move: HubSpotDealStageMove | null
      // Optional: steps saved before this field existed have no value — read with a skip fallback.
      dealNotFoundStrategy?: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_ORDER]: {
      note: Nullable<string>
      orderNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_UPDATE_ORDER_PROPERTY]: {
      fields: HubSpotFieldUpdateItem[]
      orderNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_MOVE_ORDER_TO_STAGE]: {
      move: HubSpotOrderStageMove | null
      orderNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_UPDATE_TASK_STATUS]: {
      status: HubSpotTaskStatus | null
      taskNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_ADD_CONTACT_TO_LIST]: {
      listIds: string[]
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    [AutomationStepType.ACTION_HUBSPOT_REMOVE_CONTACT_FROM_LIST]: {
      listIds: string[]
      customerNotFoundStrategy: HubSpotCustomerNotFoundStrategy
    }
    // Rules
    [AutomationStepType.RULE_DELAY]: {
      delay: Nullable<AutomationDelay>
    }
    [AutomationStepType.RULE_ADVANCED_TIME_DELAY]: {
      params: AdvancedTimeDelayParams
    }
    [AutomationStepType.RULE_CONDITIONAL_SPLIT]: AutomationStepPayloadWithAdditionalFilters
    [AutomationStepType.RULE_CONDITIONAL_SPLIT_BRANCH]: AutomationSplitBranchBasePayload
    [AutomationStepType.RULE_PERCENTAGE_SPLIT]: {}
    [AutomationStepType.RULE_PERCENTAGE_SPLIT_BRANCH]: AutomationSplitBranchBasePayload & {
      percentage: number
      isDisabled: boolean
    }
    [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS]: {
      event: Nullable<AutomationWaitUntilEventHappensPayload>
      delay: AutomationDelay
    }
    [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS_BRANCH]: AutomationSplitBranchBasePayload
    [AutomationStepType.RULE_REPLY_BASED_SPLIT]: {
      splitType: AutomationReplyBasedSplitType
      delay: AutomationDelay
    }
    [AutomationStepType.RULE_REPLY_BASED_SPLIT_BRANCH]: AutomationSplitBranchBasePayload & {
      condition: Nullable<AutomationReplyBasedSplitBranchCondition>
    }
    [AutomationStepType.RULE_GO_TO_ANOTHER_STEP]: {
      stepLocalId: Nullable<number>
      maxPasses: Nullable<number>
    }
    [AutomationStepType.RULE_GO_TO_ANOTHER_STEP_BRANCH]: AutomationSplitBranchBasePayload
    // System
    [AutomationStepType.SYSTEM_CONTACT_EXITS]: {}
  }
>

export type AutomationFlowStepsWithAdditionalFilters = {
  [T in AutomationStepType]: AutomationFlowStepPayloadMap[T] extends AutomationStepPayloadWithAdditionalFilters
    ? T
    : never
}[AutomationStepType]

export const automationFlowShopifyTriggerSteps = [
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_CREATED,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_PAID,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_FULFILLED,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_SHIPPED,
  AutomationStepType.TRIGGER_SHOPIFY_ORDER_READY_FOR_PICKUP,
  AutomationStepType.TRIGGER_SHOPIFY_ABANDONED_CHECKOUT,
] as const

export type AutomationFlowShopifyTriggerSteps = (typeof automationFlowShopifyTriggerSteps)[number]

export type ShopifyOrderValueFilter = {
  eq?: number | null
  gte?: number | null
  lte?: number | null
}

export type AutomationFlowShopifyTriggerBasePayload = {
  connectionId: number
  productIds: string[]
  categoryIds: string[]
  orderValue?: ShopifyOrderValueFilter
} & AutomationStepPayloadWithAdditionalFilters

export const automationFlowWooCommerceTriggerSteps = [
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CREATED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_PAID,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_COMPLETED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_CANCELLED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_REFUNDED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ORDER_FAILED,
  AutomationStepType.TRIGGER_WOOCOMMERCE_ABANDONED_CHECKOUT,
] as const

export type AutomationFlowWooCommerceTriggerSteps = (typeof automationFlowWooCommerceTriggerSteps)[number]

export type AutomationFlowStoreTriggerSteps = AutomationFlowShopifyTriggerSteps | AutomationFlowWooCommerceTriggerSteps

export type AutomationFlowWooCommerceTriggerBasePayload = {
  connectionId: number
  productIds: string[]
  categoryIds: string[]
  orderValue?: ShopifyOrderValueFilter
} & AutomationStepPayloadWithAdditionalFilters

// HubSpot property data type returned by the /*-properties autocomplete endpoints.
export enum HubSpotPropertyFilterType {
  TEXT = 'text',
  PHONE = 'phone',
  NUMBER = 'number',
  BOOL = 'bool',
  DATE = 'date',
  DATETIME = 'datetime',
  SINGLE_SELECT = 'single_select',
  MULTI_SELECT = 'multi_select',
}

// Internal field type carried by HubSpotUpdateContactAction field items (payload discriminator).
export enum InternalFieldType {
  TEXT = 'text',
  NUMERIC = 'numeric',
  BOOL = 'bool',
  MONETARY = 'monetary',
  DATE = 'date',
  DATE_TIME = 'dateTime',
  SINGLE_OPTION = 'singleOption',
  MULTI_OPTION = 'multiOption',
}

export enum ZohoPropertyFieldType {
  Text = 'text',
  TextArea = 'textarea',
  Email = 'email',
  Phone = 'phone',
  Website = 'website',
  Integer = 'integer',
  BigInt = 'bigint',
  Double = 'double',
  Percent = 'percent',
  Currency = 'currency',
  Date = 'date',
  DateTime = 'datetime',
  Boolean = 'boolean',
  Picklist = 'picklist',
  MultiSelectPicklist = 'multiselectpicklist',
  Lookup = 'lookup',
  OwnerLookup = 'ownerlookup',
  AutoNumber = 'autonumber',
}

const supportedHubspotPropertyFilterTypes = new Set<string>(Object.values(HubSpotPropertyFilterType))

export const isSupportedHubspotPropertyFilterType = (type: string): type is HubSpotPropertyFilterType => {
  return supportedHubspotPropertyFilterTypes.has(type)
}

export const hubspotPropertyTypeToInternalFieldType = (type: HubSpotPropertyFilterType): InternalFieldType => {
  switch (type) {
    case HubSpotPropertyFilterType.TEXT:
    case HubSpotPropertyFilterType.PHONE:
      return InternalFieldType.TEXT
    case HubSpotPropertyFilterType.NUMBER:
      return InternalFieldType.NUMERIC
    case HubSpotPropertyFilterType.BOOL:
      return InternalFieldType.BOOL
    case HubSpotPropertyFilterType.DATE:
      return InternalFieldType.DATE
    case HubSpotPropertyFilterType.DATETIME:
      return InternalFieldType.DATE_TIME
    case HubSpotPropertyFilterType.SINGLE_SELECT:
      return InternalFieldType.SINGLE_OPTION
    case HubSpotPropertyFilterType.MULTI_SELECT:
      return InternalFieldType.MULTI_OPTION
    default:
      throw new TmAbsurdError(type)
  }
}

export type HubSpotFieldValue = {
  fieldType: InternalFieldType
  value: string | number | boolean | string[] | null
}

// A single contact field update; `value` is null when the field is left unset.
export type HubSpotFieldUpdateItem = {
  key: string
  fieldType: InternalFieldType
  value: HubSpotFieldValue | null
}

export enum HubSpotTaskType {
  TODO = 'TODO',
  CALL = 'CALL',
  EMAIL = 'EMAIL',
}

export enum HubSpotTaskStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING = 'WAITING',
  COMPLETED = 'COMPLETED',
  DEFERRED = 'DEFERRED',
}

export enum HubSpotTaskPriority {
  NONE = 'NONE',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum HubSpotTaskOwnerStrategy {
  CONTACT_OWNER = 'contact_owner',
  EXPLICIT = 'explicit',
}

export enum HubSpotTaskDueDateUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
}

export type HubSpotTaskOwnerInstruction = {
  strategy: HubSpotTaskOwnerStrategy
  ownerId: Nullable<string>
}

export type HubSpotTaskDueDate = {
  value: number
  unit: HubSpotTaskDueDateUnit
}

export enum HubSpotDealPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum HubSpotDealCloseDateUnit {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
}

export type HubSpotDealCloseDate = {
  value: number
  unit: HubSpotDealCloseDateUnit
}

export enum HubSpotStageMoveStrategy {
  FORWARD = 'forward',
  BACK = 'back',
  EXPLICIT = 'explicit',
}

export type HubSpotDealStageMove = {
  strategy: HubSpotStageMoveStrategy
  dealStageId: Nullable<string>
}

export {
  HubSpotStageMoveStrategy as HubSpotDealStageMoveStrategy,
  HubSpotStageMoveStrategy as HubSpotOrderStageMoveStrategy,
}

export type HubSpotOrderStageMove = {
  strategy: HubSpotStageMoveStrategy
  orderStageId: Nullable<string>
}

export enum HubSpotTicketPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export type CrmTextFilterCriteria = {
  equals?: string | null
  notEquals?: string | null
  contains?: string | null
  notContains?: string | null
  startsWith?: string | null
  endsWith?: string | null
  isEmpty?: boolean | null
}

export type CrmNumberFilterCriteria = {
  equals?: number | null
  notEquals?: number | null
  gt?: number | null
  lt?: number | null
  gte?: number | null
  lte?: number | null
  between?: [number, number] | null
  isEmpty?: boolean | null
}

export type CrmBooleanFilterCriteria = {
  eq?: boolean | null
  isEmpty?: boolean | null
}

export type CrmDateFilterCriteria = {
  on?: string | null
  notOn?: string | null
  lt?: string | null
  gt?: string | null
  between?: [string, string] | null
  moreThanDaysAgo?: number | null
  lessThanDaysAgo?: number | null
  isNull?: boolean | null
}

export type CrmSingleSelectFilterCriteria = {
  equals?: string | null
  notEquals?: string | null
  in?: string[] | null
  notIn?: string[] | null
  isEmpty?: boolean | null
}

export type CrmMultiSelectFilterCriteria = {
  containsAny?: string[] | null
  containsAll?: string[] | null
  containsNone?: string[] | null
  isEmpty?: boolean | null
}

// A single watched property with the type-specific criteria matching its data type.
export type CrmPropertyWatchCondition = {
  propertyName: string
  text?: CrmTextFilterCriteria
  number?: CrmNumberFilterCriteria
  boolean?: CrmBooleanFilterCriteria
  date?: CrmDateFilterCriteria
  singleSelect?: CrmSingleSelectFilterCriteria
  multiSelect?: CrmMultiSelectFilterCriteria
}

export const automationFlowHubspotTriggerSteps = [
  AutomationStepType.TRIGGER_HUBSPOT_CONTACT_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_CONTACT_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_DEAL_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_TICKET_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_TICKET_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_TASK_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_ORDER_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_MEETING_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_MEETING_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_MESSAGE,
] as const

export type AutomationFlowHubspotTriggerSteps = (typeof automationFlowHubspotTriggerSteps)[number]

export const automationFlowHubspotPropertyUpdatedTriggerSteps = [
  AutomationStepType.TRIGGER_HUBSPOT_CONTACT_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_TICKET_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_MEETING_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_PROPERTY_UPDATED,
] as const

export type AutomationFlowHubspotPropertyUpdatedTriggerSteps =
  (typeof automationFlowHubspotPropertyUpdatedTriggerSteps)[number]

// HubSpot actions are only available inside a flow started by a HubSpot trigger — the trigger
// supplies the connection the action runs against. Used for isAvailableAsAChildFor gating.
export const HUBSPOT_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set(automationFlowHubspotTriggerSteps)

export const automationFlowZohoTriggerSteps = [
  AutomationStepType.TRIGGER_ZOHO_CONTACT_PROPERTY_UPDATED,
  AutomationStepType.TRIGGER_ZOHO_CONTACT_CREATED,
] as const

export type AutomationFlowZohoTriggerSteps = (typeof automationFlowZohoTriggerSteps)[number]

export const ZOHO_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set(automationFlowZohoTriggerSteps)

export const HUBSPOT_DEAL_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set([
  AutomationStepType.TRIGGER_HUBSPOT_DEAL_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED,
])

export const HUBSPOT_DEAL_WUEH_EVENT_TYPES: ReadonlySet<
  AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType
> = new Set([
  AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_DEAL_PROPERTY_CHANGED,
])

export const HUBSPOT_ORDER_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set([
  AutomationStepType.TRIGGER_HUBSPOT_ORDER_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED,
])

export const HUBSPOT_ORDER_WUEH_EVENT_TYPES: ReadonlySet<
  AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType
> = new Set([
  AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_ORDER_PROPERTY_CHANGED,
])

export const HUBSPOT_TASK_TRIGGER_TYPES: ReadonlySet<AutomationStepType> = new Set([
  AutomationStepType.TRIGGER_HUBSPOT_TASK_CREATED,
  AutomationStepType.TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED,
])

export const HUBSPOT_TASK_WUEH_EVENT_TYPES: ReadonlySet<
  AutomationWaitUntilEventHappensEventType | AutomationWaitUntilEventHappensLegacyMessageEventType
> = new Set([
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_CREATED,
  AutomationWaitUntilEventHappensEventType.HUBSPOT_TASK_PROPERTY_CHANGED,
])

export const automationFlowHubspotActionSteps = [
  AutomationStepType.ACTION_HUBSPOT_UPDATE_CONTACT,
  AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_CONTACT,
  AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_DEAL,
  AutomationStepType.ACTION_HUBSPOT_CREATE_TASK,
  AutomationStepType.ACTION_HUBSPOT_CREATE_TICKET,
  AutomationStepType.ACTION_HUBSPOT_CREATE_DEAL,
  AutomationStepType.ACTION_HUBSPOT_MOVE_DEAL_TO_STAGE,
  AutomationStepType.ACTION_HUBSPOT_ADD_NOTE_TO_ORDER,
  AutomationStepType.ACTION_HUBSPOT_UPDATE_ORDER_PROPERTY,
  AutomationStepType.ACTION_HUBSPOT_MOVE_ORDER_TO_STAGE,
  AutomationStepType.ACTION_HUBSPOT_UPDATE_TASK_STATUS,
  AutomationStepType.ACTION_HUBSPOT_ADD_CONTACT_TO_LIST,
  AutomationStepType.ACTION_HUBSPOT_REMOVE_CONTACT_FROM_LIST,
] as const

export type AutomationFlowHubspotActionSteps = (typeof automationFlowHubspotActionSteps)[number]

export type AutomationFlowHubspotPropertyUpdatedSteps =
  | AutomationStepType.TRIGGER_HUBSPOT_CONTACT_PROPERTY_UPDATED
  | AutomationStepType.TRIGGER_HUBSPOT_DEAL_PROPERTY_UPDATED
  | AutomationStepType.TRIGGER_HUBSPOT_TICKET_PROPERTY_UPDATED
  | AutomationStepType.TRIGGER_HUBSPOT_TASK_PROPERTY_UPDATED
  | AutomationStepType.TRIGGER_HUBSPOT_ORDER_PROPERTY_UPDATED
  | AutomationStepType.TRIGGER_HUBSPOT_MEETING_PROPERTY_UPDATED
  | AutomationStepType.TRIGGER_HUBSPOT_CONVERSATION_PROPERTY_UPDATED

// HubSpot enum values sent in the trigger payloads (mirror the Swagger enums).
export enum HubSpotContactSource {
  ACADEMY = 'ACADEMY',
  IMPORT = 'IMPORT',
  API = 'API',
  CRM_UI = 'CRM_UI',
  INTERNAL_PROCESSING = 'INTERNAL_PROCESSING',
  ANALYTICS = 'ANALYTICS',
  CONVERSATIONS = 'CONVERSATIONS',
  EMAIL = 'EMAIL',
  CALCULATED = 'CALCULATED',
  FORM = 'FORM',
  MEETINGS = 'MEETINGS',
  EXTENSION = 'EXTENSION',
}

export enum HubSpotRecordSource {
  CRM_UI = 'CRM_UI',
  CRM_BULK_ACTION = 'CRM_BULK_ACTION',
  IMPORT = 'IMPORT',
  BATCH_UPDATE = 'BATCH_UPDATE',
  RECORD_CLONE = 'RECORD_CLONE',
  RESTORE_TOOL = 'RESTORE_TOOL',
  FORMS = 'FORMS',
  ANALYTICS = 'ANALYTICS',
  ADS = 'ADS',
  CONTENT_MEMBERSHIP = 'CONTENT_MEMBERSHIP',
  FEEDBACK = 'FEEDBACK',
  MEETINGS = 'MEETINGS',
  CONVERSATIONS = 'CONVERSATIONS',
  BOT_CHATFLOW = 'BOT_CHATFLOW',
  CUSTOMER_AGENT = 'CUSTOMER_AGENT',
  CONNECTED_INBOX = 'CONNECTED_INBOX',
  BCC_TO_CRM = 'BCC_TO_CRM',
  FORWARD_TO_CRM = 'FORWARD_TO_CRM',
  INBOUND_OUTBOUND_CALLFLOW = 'INBOUND_OUTBOUND_CALLFLOW',
  WORKFLOW = 'WORKFLOW',
  INTEGRATION = 'INTEGRATION',
  MARKETPLACE = 'MARKETPLACE',
  SALESFORCE = 'SALESFORCE',
  ECOMMERCE_INTEGRATION = 'ECOMMERCE_INTEGRATION',
  CONNECTED_ACCOUNT = 'CONNECTED_ACCOUNT',
  HUBSPOT_PROCESSING = 'HUBSPOT_PROCESSING',
  SALES_EXTENSION = 'SALES_EXTENSION',
  MOBILE_IOS = 'MOBILE_IOS',
  MOBILE_ANDROID = 'MOBILE_ANDROID',
  REVENUE_HUB = 'REVENUE_HUB',
}

export enum HubSpotConversationChannel {
  CHANNEL_1000 = '1000',
  CHANNEL_1001 = '1001',
  CHANNEL_1002 = '1002',
  CHANNEL_1003 = '1003',
  CHANNEL_1004 = '1004',
  CHANNEL_1007 = '1007',
  CHANNEL_1008 = '1008',
  CHANNEL_1009 = '1009',
  CHANNEL_1010 = '1010',
  CHANNEL_1011 = '1011',
}

export enum HubSpotConversationMessageDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export type HubSpotDealValueFilterCriteria = {
  eq?: number | null
  gte?: number | null
  lte?: number | null
}

export type AutomationFlowHubspotContactPropertyChangedPayload = {
  connectionId: number
  propertiesToWatch: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotDealPropertyChangedPayload = AutomationFlowHubspotContactPropertyChangedPayload

export type AutomationFlowHubspotTicketPropertyChangedPayload = AutomationFlowHubspotContactPropertyChangedPayload

export type AutomationFlowHubspotMeetingPropertyChangedPayload = AutomationFlowHubspotContactPropertyChangedPayload

export type AutomationFlowHubspotConversationPropertyChangedPayload = AutomationFlowHubspotContactPropertyChangedPayload

export type AutomationFlowHubspotContactCreatedPayload = {
  connectionId: number
  lifecycleStages: string[]
  sources: HubSpotContactSource[]
  ownerIds: string[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotDealCreatedPayload = {
  connectionId: number
  pipelines: string[]
  dealStages: string[]
  priorities: HubSpotDealPriority[]
  dealValue: HubSpotDealValueFilterCriteria | null
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotTicketCreatedPayload = {
  connectionId: number
  pipelines: string[]
  ticketStages: string[]
  priorities: HubSpotTicketPriority[]
  recordSources: HubSpotRecordSource[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotTaskCreatedPayload = {
  connectionId: number
  statuses: HubSpotTaskStatus[]
  types: HubSpotTaskType[]
  priorities: HubSpotTaskPriority[]
  ownerIds: string[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export enum HubSpotMeetingLocationType {
  PHONE = 'PHONE',
  ADDRESS = 'ADDRESS',
  CUSTOM = 'CUSTOM',
  VCE = 'VCE',
}

export type AutomationFlowHubspotMeetingCreatedPayload = {
  connectionId: number
  activityTypes: string[]
  locationTypes: HubSpotMeetingLocationType[]
  startTime: CrmDateFilterCriteria | null
  endTime: CrmDateFilterCriteria | null
  ownerIds: string[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotTaskPropertyChangedPayload = AutomationFlowHubspotContactPropertyChangedPayload

export type AutomationFlowHubspotOrderPropertyChangedPayload = AutomationFlowHubspotContactPropertyChangedPayload

export type HubSpotOrderValueFilterCriteria = HubSpotDealValueFilterCriteria

export type AutomationFlowHubspotOrderCreatedPayload = {
  connectionId: number
  pipelines: string[]
  stages: string[]
  sourceStores: string[]
  orderValue: HubSpotOrderValueFilterCriteria | null
  recordSources: HubSpotRecordSource[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotConversationCreatedPayload = {
  connectionId: number
  inboxIds: string[]
  channels: HubSpotConversationChannel[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowHubspotConversationMessagePayload = {
  connectionId: number
  inboxIds: string[]
  direction: HubSpotConversationMessageDirection | null
  channels: HubSpotConversationChannel[]
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowZohoContactPropertyChangedPayload = {
  connectionId: number
  propertiesToWatch: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationFlowZohoContactCreatedPayload = {
  connectionId: number
  propertyFilters: CrmPropertyWatchCondition[]
} & AutomationStepPayloadWithAdditionalFilters

export type AutomationSplitBranchStepTypes = {
  [T in AutomationStepType]: AutomationFlowStepPayloadMap[T] extends AutomationSplitBranchBasePayload ? T : never
}[AutomationStepType]

const automationSplitBranchStepTypeMap = {
  [AutomationStepType.RULE_PERCENTAGE_SPLIT_BRANCH]: true,
  [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS_BRANCH]: true,
  [AutomationStepType.RULE_CONDITIONAL_SPLIT_BRANCH]: true,
  [AutomationStepType.RULE_REPLY_BASED_SPLIT_BRANCH]: true,
  [AutomationStepType.RULE_GO_TO_ANOTHER_STEP_BRANCH]: true,
} as const satisfies Record<AutomationSplitBranchStepTypes, true>

export const automationSplitBranchStepTypes = Object.keys(
  automationSplitBranchStepTypeMap,
) as AutomationSplitBranchStepTypes[]

export type AutomationFlowStepPayloadMapPrepared = {
  [P in keyof AutomationFlowStepPayloadMap]: { type: P } & AutomationFlowStepPayloadMap[P]
}
export type AutomationFlowStepPayload<Type extends AutomationStepType> = AutomationFlowStepPayloadMapPrepared[Type]

export enum AutomationGoToAnotherStepBranchType {
  GO_TO = 'goTo',
  SKIP = 'skip',
}

export interface AutomationFlowStatistics {
  enteredCount: number
  inProgressCount: number
  errorsCount: number
  completedCount: number
}

export interface AutomationFlowInterface {
  id: string
  name: string
  status: AutomationFlowStatus
  settings: AutomationFlowSettings
  stepsWithMessagesCount: number
  stepsCount: number
  createdBy: UserPersonalInfo
  updatedAt: string
  lastTriggeredAt: string
  hasContactsInFlow: boolean
  stats: AutomationFlowStatistics
}

export interface AutomationFlowStepInterface<Type extends AutomationStepType = AutomationStepType> {
  id: string // ID from BE
  localId: number // Generated by FE
  automationId: string
  parents: number[] // localIds of parent steps
  title: string
  payload: AutomationFlowStepPayload<Type>
  createdAt: string
  updatedAt: string
}

export type AbstractAutomationFlowStepInterface = Omit<AutomationFlowStepInterface, 'payload'> & {
  payload: Record<string, unknown>
}

export type AutomationFlowStepFromResponse<Type extends AutomationStepType = AutomationStepType> =
  AutomationFlowStepInterface<Type>

export type AutomationFlowStepToRequest<Type extends AutomationStepType = AutomationStepType> = Omit<
  AutomationFlowStepInterface<Type>,
  'id' | 'createdAt' | 'updatedAt' | 'automationId'
>

export type AutomationFlowSingleStepUpdateRequest<Type extends AutomationStepType = AutomationStepType> = {
  title: string
  payload?: AutomationFlowStepPayload<Type>
}

export interface AutomationFlowCreationRequest {
  name: string
  status: AutomationFlowStatus
  steps: AutomationFlowStepToRequest[]
  settings: AutomationFlowSettings
}

export type AutomationFlowUpdateRequest = AutomationFlowCreationRequest

export interface AutomationFlowNameAndSettingsUpdateRequest {
  name: string
  settings: AutomationFlowSettings
}

export const AUTOMATION_STEP_NAME_MAX_LENGTH = 50

export enum AutomationLogStatus {
  Success = 'success',
  Failed = 'failed',
  Waiting = 'waiting',
  Skipped = 'skipped',
  Exited = 'exited',
}

export type AutomationStepStatisticsStatus = AutomationLogStatus

export interface AutomationFlowStepStatisticsInterface {
  stepId: number
  successCount: number
  failedCount: number
  skippedCount: number
  waitingCount: number
  exitedCount: number
  jumpedCount: number
}

export interface AutomationFlowStepStatisticsResponse {
  items: AutomationFlowStepStatisticsInterface[]
}

type AutomationLogEntityRecordDefault<T extends Record<string, unknown>> = Omit<
  Record<AutomationStepType, null>,
  keyof T
> &
  T

interface IAutomationLogEntityBase<T> {
  _type: T
}

interface IWaitUntilEventHappensEntityContact extends IAutomationLogEntityBase<'contact'> {
  id: number
}

export interface IWaitUntilEventHappensEntityOutboundEmail extends IAutomationLogEntityBase<'outbound_email'> {
  emailSourceId: number
  id: number
}

export interface IAutomationLogEntityInboundEmail extends IAutomationLogEntityBase<'inbound_ticket_message'> {
  id: number // ticket message id
  ticketId: number
}

export interface IAutomationLogEntityInboundSms extends IAutomationLogEntityBase<'inbound_message'> {
  id: number // sms id
}

interface IAutomationFbIgMessageEntity {
  id: number
  chatId: number
  accountId: number
}

interface IAutomationWhatsappMessageEntity {
  id: number
  chatId: number
  accountId: number
  phoneId: number
}

export type AutomationLogEntityRecord = AutomationLogEntityRecordDefault<{
  [AutomationStepType.TRIGGER_NEW_INBOUND_SMS_RECEIVED]: { id: number }
  [AutomationStepType.TRIGGER_NEW_INBOUND_EMAIL_RECEIVED]: { id: number }
  [AutomationStepType.TRIGGER_NEW_INBOUND_LIVE_CHAT_MESSAGE_RECEIVED]: {
    liveChatMessageId: number
    webWidgetId: string
    chatId: number
  }
  [AutomationStepType.TRIGGER_NEW_INBOUND_WHATSAPP_MESSAGE_RECEIVED]: IAutomationWhatsappMessageEntity
  [AutomationStepType.TRIGGER_NEW_INBOUND_FACEBOOK_MESSAGE_RECEIVED]: IAutomationFbIgMessageEntity
  [AutomationStepType.TRIGGER_NEW_INBOUND_INSTAGRAM_MESSAGE_RECEIVED]: IAutomationFbIgMessageEntity
  [AutomationStepType.ACTION_SEND_SMS]: { id: number }
  [AutomationStepType.ACTION_SEND_EMAIL]: { id: number; emailSourceId: number }
  [AutomationStepType.ACTION_CREATE_NEW_TASK]: { id: number }
  [AutomationStepType.ACTION_CREATE_NEW_DEAL]: { id: number }
  [AutomationStepType.ACTION_SEND_EMAIL_REPLY]: { id: number }
  [AutomationStepType.ACTION_SEND_WHATSAPP_REPLY]: IAutomationWhatsappMessageEntity
  [AutomationStepType.ACTION_SEND_FACEBOOK_REPLY]: IAutomationFbIgMessageEntity
  [AutomationStepType.ACTION_SEND_INSTAGRAM_REPLY]: IAutomationFbIgMessageEntity
  [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS]:
    | IWaitUntilEventHappensEntityContact
    | IWaitUntilEventHappensEntityOutboundEmail
    | IAutomationLogEntityInboundSms
    | IAutomationLogEntityInboundEmail
  [AutomationStepType.RULE_REPLY_BASED_SPLIT]: IAutomationLogEntityInboundEmail | IAutomationLogEntityInboundSms
}>

type AutomationLogDetailsRecordDefault<T extends Record<string, unknown>> = Omit<
  Record<AutomationStepType, {}>,
  keyof T
> &
  T

interface IAutomationLogDetailsChosenStep {
  title: string
}

export type IAutomationLogDetailsReplyBasedStepBranch = Pick<
  AutomationFlowStepInterface<AutomationStepType.RULE_REPLY_BASED_SPLIT_BRANCH>,
  'id' | 'title' | 'payload'
> & {
  isDeleted?: false
}

export type IAutomationLogDetailsReplyBasedStepBranchDeleted = {
  id: string
  isDeleted: true
}

export const isAutomationLogDetailsReplyBasedStepBranch = (
  branch:
    | IAutomationLogDetailsReplyBasedStepBranch
    | IAutomationLogDetailsReplyBasedStepBranchDeleted
    | null
    | undefined,
): branch is IAutomationLogDetailsReplyBasedStepBranch => {
  return !!branch && !branch.isDeleted
}

type AutomationLogDetailsCommon<T> = {
  [P in keyof T]: T[P] & {
    stoppedBy?: Nullable<AutomationLogPreview>
    stoppedByUser?: Nullable<UserPersonalInfo>
  }
}

type AutomationLogDetailsRecordWithSpecificParams = AutomationLogDetailsRecordDefault<{
  [AutomationStepType.RULE_CONDITIONAL_SPLIT]: {
    chosenStep: IAutomationLogDetailsChosenStep
    isFiltersMatched: boolean
  }
  [AutomationStepType.RULE_PERCENTAGE_SPLIT]: {
    chosenStep: IAutomationLogDetailsChosenStep
  }
  [AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS]: {
    chosenStep: IAutomationLogDetailsChosenStep
    waitingUntil: string | null
    isFired: boolean
  }
  [AutomationStepType.RULE_DELAY]: {
    waitingUntil: string | null
  }
  [AutomationStepType.RULE_ADVANCED_TIME_DELAY]: {
    waitingUntil: string | null
    customFieldValue: {
      value: string
      fieldType: 'date' | 'dateTime'
    } | null
  }
  [AutomationStepType.ACTION_SEND_SMS]: {
    nextSendAt: string | null
  }
  [AutomationStepType.ACTION_SEND_EMAIL]: {
    nextSendAt: string | null
  }
  [AutomationStepType.RULE_REPLY_BASED_SPLIT]: {
    chosenStep: Nullable<IAutomationLogDetailsReplyBasedStepBranch | IAutomationLogDetailsReplyBasedStepBranchDeleted>
    waitingUntil: string | null
    isReplyHappens: boolean
    stepBranches: IAutomationLogDetailsReplyBasedStepBranch[]
  }
  [AutomationStepType.ACTION_SEND_WEBHOOK]: {
    hasRequestBody: boolean
    hasResponseBody: boolean
    method: WebhookMethod
    statusCode: Nullable<number>
    url: string
  }
}>

export type AutomationLogDetailsRecord = AutomationLogDetailsCommon<AutomationLogDetailsRecordWithSpecificParams>

export type AutomationUpdatableSplitBranchStepType = AutomationStepType.RULE_PERCENTAGE_SPLIT_BRANCH

export type AutomationSplitBranchesUpdateRequest = {
  branches: Pick<AutomationFlowStepInterface<AutomationUpdatableSplitBranchStepType>, 'id' | 'title' | 'payload'>[]
}

export const isShopifyTriggerStep = (
  step: AutomationFlowStepInterface<AutomationStepType>,
): step is AutomationFlowStepInterface<AutomationFlowShopifyTriggerSteps> => {
  return automationFlowShopifyTriggerSteps.includes(step.payload.type as AutomationFlowShopifyTriggerSteps)
}

export const isShopifyTemplateTriggerStep = (step: AutomationTemplateStep): boolean => {
  return automationFlowShopifyTriggerSteps.includes(step.payload.type as AutomationFlowShopifyTriggerSteps)
}

export const isExternalIntegrationTemplateStep = (step: AutomationTemplateStep): boolean => {
  const { payload } = step

  if (
    automationFlowShopifyTriggerSteps.includes(payload.type as AutomationFlowShopifyTriggerSteps) ||
    automationFlowWooCommerceTriggerSteps.includes(payload.type as AutomationFlowWooCommerceTriggerSteps)
  ) {
    return true
  }

  if (payload.type !== AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS) {
    return false
  }

  const { event } = payload as AutomationFlowStepPayload<AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS>

  return (
    isAutomationWaitUntilEventHappensShopifyEventPayload(event) ||
    isAutomationWaitUntilEventHappensWooCommerceEventPayload(event)
  )
}

export const isWooCommerceTriggerStep = (
  step: AutomationFlowStepInterface<AutomationStepType>,
): step is AutomationFlowStepInterface<AutomationFlowWooCommerceTriggerSteps> => {
  return automationFlowWooCommerceTriggerSteps.includes(step.payload.type as AutomationFlowWooCommerceTriggerSteps)
}

export const isHubspotTriggerStep = (
  step: AutomationFlowStepInterface<AutomationStepType>,
): step is AutomationFlowStepInterface<AutomationFlowHubspotTriggerSteps> => {
  return HUBSPOT_TRIGGER_TYPES.has(step.payload.type)
}

export const isZohoTriggerStep = (
  step: AutomationFlowStepInterface<AutomationStepType>,
): step is AutomationFlowStepInterface<AutomationFlowZohoTriggerSteps> => {
  return ZOHO_TRIGGER_TYPES.has(step.payload.type)
}

export const isExternalIntegrationTriggerStep = (
  step: AutomationFlowStepInterface<AutomationStepType>,
): step is AutomationFlowStepInterface<AutomationFlowShopifyTriggerSteps | AutomationFlowWooCommerceTriggerSteps> => {
  return isShopifyTriggerStep(step) || isWooCommerceTriggerStep(step)
}

export const isWUEHNoBranch = (
  step: AutomationFlowStepInterface<AutomationStepType> | null,
): step is AutomationFlowStepInterface<AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS_BRANCH> => {
  return (
    step?.payload.type === AutomationStepType.RULE_WAIT_UNTIL_EVENT_HAPPENS_BRANCH &&
    step.payload.sorting !== WUEH_HAPPENED_BRANCH_SORTING
  )
}

export const isLogWithNonNullableEntity = <T extends { entity: unknown }>(log: T): log is NonNullableBy<T, 'entity'> =>
  !isNil(log.entity)

export const isLogWithNonNullableDetails = <T extends { details: unknown }>(
  log: T,
): log is NonNullableBy<T, 'details'> => !isNil(log.details)

export interface ITagForFlow {
  localId: number
  variables: string[]
}

export interface TrackedEmailLink {
  link: string
  title: Nullable<string>
}

export enum WebhookMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export enum WebhookBodyType {
  NONE = 'none',
  JSON = 'json',
  FORM = 'form',
  TEXT = 'text',
}

export enum WebhookFailurePolicy {
  STOP = 'stop',
  SKIP = 'skip',
}

export type WebhookKeyValue<Value = string> = {
  key: string
  value: Value
}

export enum WebhookAuthApiKeyPlace {
  HEADER = 'header',
  QUERY = 'query',
}

export enum WebhookAuthType {
  NONE = 'none',
  BEARER = 'bearer',
  BASIC = 'basic',
  API_KEY = 'apiKey',
}

type WebhookAuthNone = {
  type: WebhookAuthType.NONE
}

type WebhookAuthBearer = {
  type: WebhookAuthType.BEARER
  tokenRef: string // ref of user bearer token
}

type WebhookAuthBasic = {
  type: WebhookAuthType.BASIC
  username: string
  tokenRef: string // ref of password
}

type WebhookAuthApiKey = {
  type: WebhookAuthType.API_KEY
  in: WebhookAuthApiKeyPlace
  keyName: string // example: X-Api-Key
  tokenRef: string // ref of key
}

export type WebhookAuth = WebhookAuthNone | WebhookAuthBearer | WebhookAuthBasic | WebhookAuthApiKey

export enum WebhookSecretKind {
  AUTH_TOKEN = 'webhookAuthToken',
  SIGNING_SECRET = 'webhookSigningSecret',
}

type WebhookSigningSecretCreationRequest = {
  kind: WebhookSecretKind.SIGNING_SECRET
}

type WebhookSigningSecretUpdatingRequest = {
  kind: WebhookSecretKind.SIGNING_SECRET
  ref: string
}

type WebhookAuthTokenCreationRequest = {
  kind: WebhookSecretKind.AUTH_TOKEN // TODO add basic auth and api key
  token: string
}

type WebhookAuthTokenUpdatingRequest = {
  kind: WebhookSecretKind.AUTH_TOKEN // TODO add basic auth and api key
  ref: string
  token: string
}

export type WebhookSecretCreationOrUpdatingRequest =
  | WebhookSigningSecretCreationRequest
  | WebhookSigningSecretUpdatingRequest
  | WebhookAuthTokenCreationRequest
  | WebhookAuthTokenUpdatingRequest

export type WebhookSecretCreationOrUpdatingResponse = {
  ref: string
  secret: string
  hint: string
}

export type WebhookSecretGettingResponse = {
  ref: string
  hint: string
}

export type WebhookTestRequest = {
  payload: AutomationFlowStepPayloadMap[AutomationStepType.ACTION_SEND_WEBHOOK]
}

export type WebhookTestRequestResponse = {
  ok: boolean
  statusCode: number
  body: string
  durationMs: number
  errorType: Nullable<'WebhookRequestFailed' | 'WebhookUnexpectedStatus'>
  errorMessage: Nullable<string>
}
